import { useQueryClient } from '@tanstack/react-query'

import {
  useNotesServiceDeleteV1IndividualsByIndividualIdNotesByNoteIdEventsByEventId,
  useNotesServicePatchV1IndividualsByIndividualIdNotesByNoteIdEventsByEventId,
} from '../client/queries'
import {
  useNotesServiceGetV1IndividualsByIndividualIdNotesByNoteIdKey,
  useNotesServiceGetV1IndividualsByIndividualIdNotesKey,
  useTimelineServiceGetV1IndividualsByIndividualIdTimelineKey,
} from '../client/queries/common'

export type ConfirmPendingEventInput = {
  eventId: string
  year: number
  month?: number
  day?: number
}

// Confirm (→ dated activity) or dismiss a note event via the unified events API:
//   confirm = PATCH .../events/:eventId { year, month?, day? }  (a year re-buckets it
//             from pendingEvents to dated activities)
//   dismiss = DELETE .../events/:eventId
// The same endpoints can later patch description/type or adjust any event's date.
// On success we invalidate the note, the notes list, and the timeline — confirming
// removes the pending event AND adds an activity the main timeline now surfaces.
export const useUpdatePendingEvent = (individualId?: string, noteId?: string) => {
  const queryClient = useQueryClient()

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: [useNotesServiceGetV1IndividualsByIndividualIdNotesByNoteIdKey],
    })
    queryClient.invalidateQueries({
      queryKey: [useNotesServiceGetV1IndividualsByIndividualIdNotesKey],
    })
    queryClient.invalidateQueries({
      queryKey: [useTimelineServiceGetV1IndividualsByIndividualIdTimelineKey],
    })
  }

  const updateMutation =
    useNotesServicePatchV1IndividualsByIndividualIdNotesByNoteIdEventsByEventId({
      onSuccess: invalidate,
    })
  const deleteMutation =
    useNotesServiceDeleteV1IndividualsByIndividualIdNotesByNoteIdEventsByEventId({
      onSuccess: invalidate,
    })

  const confirm = async ({ eventId, year, month, day }: ConfirmPendingEventInput) => {
    if (!individualId || !noteId) return
    await updateMutation.mutateAsync({
      eventId,
      individualId,
      noteId,
      requestBody: { year, month, day },
    })
  }

  const dismiss = async (eventId: string) => {
    if (!individualId || !noteId) return
    await deleteMutation.mutateAsync({ eventId, individualId, noteId })
  }

  return {
    confirm,
    dismiss,
    isPending: updateMutation.isPending || deleteMutation.isPending,
  }
}
